import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import useAPI from "./useAPI";

export default function useTranslationAdmin(defaultProjects = []) {
  const api = useAPI();

  const [projects, setProjects] = useState(defaultProjects);
  const [languages, setLanguages] = useState([]);
  const [terms, setTerms] = useState([]);
  const [translations, setTranslations] = useState([]);

  const [selectedProject, setSelectedProject] = useState(defaultProjects[0] || null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  useEffect(() => {
    if (!selectedProject?.id) return;

    setLanguages([]);
    setTerms([]);
    setTranslations([]);
    setSelectedLanguage(null);

    loadLanguages(selectedProject.id).then((langs = []) => {
      if (Array.isArray(langs) && langs.length > 0) {
        langs.forEach((lang) => {
          if (lang?.code) {
            loadTranslations(selectedProject.id, lang.code, { merge: true });
          }
        });
      }
    });
    loadTerms(selectedProject.id);
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!selectedProject?.id || !selectedLanguage?.code) return;
    loadTranslations(selectedProject.id, selectedLanguage.code, { merge: true });
  }, [selectedLanguage?.code]);

  const loadLanguages = async (projectId) => {
    try {
      const langs = await api.getAllLanguages(projectId);
      setLanguages(langs);
      return langs;
    } catch (e) {
      toast.error("Error loading languages");
    }
  };

  const loadTerms = async (projectId) => {
    try {
      const newTerms = await api.getAllTerms(projectId);
      setTerms(newTerms);
      return newTerms;
    } catch (e) {
      toast.error("Error loading terms");
    }
  };

  const getTranslationKey = (item) => {
    const termKey = item?.termCode ?? item?.termId ?? "";
    const languageKey = item?.languageCode ?? item?.languageId ?? "";
    return `${termKey}-${languageKey}`;
  };

  const mergeTranslations = (prev, incoming) => {
    const map = new Map();
    prev.forEach((item) => map.set(getTranslationKey(item), item));
    incoming.forEach((item) => map.set(getTranslationKey(item), item));
    return Array.from(map.values());
  };

  const normalizeTranslation = (item, fallbackLanguageCode) => ({
    termCode: item?.termCode ?? item?.term_code ?? item?.term ?? item?.code ?? "",
    termId: item?.termId ?? item?.term_id ?? "",
    languageCode: item?.languageCode ?? item?.language_code ?? fallbackLanguageCode ?? item?.language ?? "",
    languageId: item?.languageId ?? item?.language_id ?? "",
    translation: item?.translation ?? item?.value ?? "",
  });

  const loadTranslations = async (projectId, languageCode, options = {}) => {
    try {
      const response = await api.getTranslation(projectId, languageCode);

      let parsed = [];

      if (Array.isArray(response)) {
        parsed = response.map((item) => normalizeTranslation(item, languageCode));
      } else if (Array.isArray(response?.translations)) {
        parsed = response.translations.map((item) => normalizeTranslation(item, languageCode));
      } else if (response && typeof response === "object") {
        parsed = Object.entries(response)
          .filter(([key]) => key !== "success" && key !== "message")
          .map(([termCode, translation]) => ({
            termCode,
            languageCode,
            translation,
          }));
      }

      if (options.merge) {
        setTranslations((prev) => mergeTranslations(prev, parsed));
      } else {
        setTranslations(parsed);
      }
      return parsed;
    } catch (e) {
      toast.error("Error loading translations");
    }
  };


  const selectProject = (project) => {
    setSelectedProject(project);
  };

  const selectLanguage = (language) => {
    setSelectedLanguage(language);
  };

  const upsertLanguage = async (data) => {
    if (!selectedProject?.id) return toast.error("Please select a project first");
    if (!data.code) return toast.error("Please enter a language code");
    if (!data.name) return toast.error("Please enter a language name");

    const newLanguage = await api.createOrUpdateLanguage(
      selectedProject.id,
      data
    );

    setLanguages((prev) => {
      const exists = prev.find((l) => l.code === newLanguage.code);
      return exists
        ? prev.map((l) => (l.code === newLanguage.code ? newLanguage : l))
        : [...prev, newLanguage];
    });
  };

  const deleteLanguage = async (languageCode) => {
    if (!selectedProject?.id) return toast.error("Please select a project first");

    await api.deleteLanguage(selectedProject.id, languageCode);

    setLanguages((prev) => prev.filter((l) => l.code !== languageCode));
    if (selectedLanguage?.code === languageCode) {
      setSelectedLanguage(null);
      setTranslations([]);
    }
  };

  const upsertTerm = async (data) => {
    if (!selectedProject?.id) return toast.error("Please select a project first");
    if (!data.term) return toast.error("Please enter a term");

    const newTerm = await api.createOrUpdateTerm(selectedProject.id, data);

    setTerms((prev) => {
      const exists = prev.find((t) => t.id === newTerm.id);
      return exists
        ? prev.map((t) => (t.id === newTerm.id ? newTerm : t))
        : [...prev, newTerm];
    });
  };

  const deleteTerm = async (term) => {
    if (!selectedProject?.id) return toast.error("Please select a project first");

    const termCode = term?.code ?? term?.term ?? term;
    await api.deleteTerm(selectedProject.id, termCode);
    setTerms((prev) => prev.filter((t) => (t.code ?? t.term) !== termCode));
  };

  const upsertTranslation = async (data) => {
    const { termId, languageId, termCode, languageCode, translation } = data;

    if (!termId) return toast.error("Please select a term first");
    if (!languageId) return toast.error("Please select a language first");

    const normalizedTranslation = translation ?? "";

    if (normalizedTranslation.trim() === "") {
      await api.deleteTranslation(selectedProject.id, termId, languageId);
      await api.clearTranslationCache(selectedProject.id);
      setTranslations((prev) =>
        prev.filter((t) => {
          if (termCode && languageCode) {
            return !(t.termCode === termCode && t.languageCode === languageCode);
          }
          return !(t.termId === termId && t.languageId === languageId);
        })
      );
      return;
    }

    await api.createOrUpdateTranslation(selectedProject.id, { ...data, translation: normalizedTranslation });
    await api.clearTranslationCache(selectedProject.id);

    setTranslations((prev) => {
      const exists = prev.find((t) => {
        if (termCode && languageCode) {
          return t.termCode === termCode && t.languageCode === languageCode;
        }
        return t.termId === termId && t.languageId === languageId;
      });

      if (exists) {
        return prev.map((t) =>
          (termCode && languageCode
            ? t.termCode === termCode && t.languageCode === languageCode
            : t.termId === termId && t.languageId === languageId)
            ? { ...t, translation: normalizedTranslation, termCode: termCode ?? t.termCode, languageCode: languageCode ?? t.languageCode }
            : t
        );
      }

      return [...prev, { termId, languageId, termCode, languageCode, translation: normalizedTranslation }];
    });
  };

  const deleteTranslation = async (termId, languageId) => {
    if (!selectedProject?.id) return toast.error("Please select a project first");

    await api.deleteTranslation(selectedProject.id, termId, languageId);

    setTranslations((prev) =>
      prev.filter(
        (t) => !(t.termId === termId && t.languageId === languageId)
      )
    );
  };

  const translateTerm = async ({ termId, termCode, languageId, languageCode, silent = false }) => {
    if (!selectedProject?.id) return toast.error("Please select a project first");
    if (!termId || !languageId) return toast.error("Missing term or language");

    try {
      const result = await api.translateTerm(selectedProject.id, termId, languageId);

      // The endpoint returns { translation: { translation: "...", ... } } or { translation: "..." }
      const raw = result?.translation;
      const translatedText = typeof raw === "object" ? raw?.translation : raw;

      if (!translatedText) {
        if (!silent) toast.error("No translation returned");
        return;
      }

      // Endpoint already persists — just clear cache and update local state
      await api.clearTranslationCache(selectedProject.id);

      setTranslations((prev) => {
        const key = `${termCode || termId}-${languageCode || languageId}`;
        const exists = prev.find((t) => getTranslationKey(t) === key);
        if (exists) {
          return prev.map((t) =>
            getTranslationKey(t) === key ? { ...t, translation: translatedText } : t
          );
        }
        return [...prev, { termId, languageId, termCode, languageCode, translation: translatedText }];
      });

      if (!silent) toast.success("Translation generated");
    } catch (e) {
      if (!silent) toast.error("Failed to translate");
      else throw e;
    }
  };

  const refreshTranslations = async () => {
    await api.clearTranslationCache(selectedProject.id);
    toast.success("Translations refreshed successfully");
  };

  return {
    projects,
    setProjects,
    languages,
    terms,
    translations,
    selectedProject,
    selectedLanguage,
    selectProject,
    selectLanguage,
    upsertLanguage,
    deleteLanguage,
    upsertTerm,
    deleteTerm,
    upsertTranslation,
    deleteTranslation,
    translateTerm,
    refreshTranslations,
  };
}
